import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, Clock, MapPin, MessageCircle } from "lucide-react";
import { companyInfo } from "../data/mock";

const REVIEW_KEY = "google_review_banner_dismissed";
const BANNER_H = "52px";

const navItems = [
  { label: "Acasă", path: "/" },
  { label: "Despre Noi", path: "/despre-noi" },
  { label: "Servicii", path: "/servicii" },
  { label: "Produse", path: "/produse" },
  { label: "Ajutor Înmormântare", path: "/ajutor-inmormantare-2026" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
];

const EmergencyBar = () => (
  <div className="emergency-bar" data-testid="emergency-bar">
    <div className="max-w-[1140px] mx-auto px-4 flex items-center justify-between">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" />
          <span className="font-medium tracking-wide uppercase text-xs">URGENȚĂ NON-STOP</span>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5 opacity-60" />
          <span className="text-xs opacity-80">Constanța & Lumina</span>
        </div>
      </div>
      <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:opacity-80 text-white no-underline">
        <Phone className="w-3.5 h-3.5 animate-pulse-subtle" style={{ color: "var(--gold)" }} />
        <span className="font-semibold text-xs tracking-wide">{companyInfo.phone}</span>
      </a>
    </div>
  </div>
);

const ReviewBanner = ({ onDismiss, bannerRef }) => (
  <div
    ref={bannerRef}
    style={{ backgroundColor: "#141414", borderBottom: "1px solid rgba(198,168,90,0.18)" }}
    data-testid="google-review-banner"
  >
    {/* ── MOBILE layout: centered stacked ── */}
    <div className="sm:hidden px-5 py-5 relative">
      <button
        onClick={onDismiss}
        className="absolute top-3 right-4 p-1.5 text-white/30 hover:text-white/70 bg-transparent border-none cursor-pointer"
        aria-label="Închide"
        data-testid="review-banner-close"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="text-center pr-6">
        <div className="flex justify-center gap-1 mb-2">
          {[...Array(5)].map((_, i) => (
            <svg key={i} className="w-4 h-4" fill="#C6A85A" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
        <p className="text-white text-base font-semibold mb-1">Ne-ați fost alături?</p>
        <p className="text-white/50 text-sm mb-4 leading-snug">Recenzia dvs. ajută și alte familii din Constanța.</p>
        <a
          href="https://g.page/r/CRbaHIZUjJVVEBM/review"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full no-underline font-semibold text-base transition-opacity hover:opacity-90"
          style={{ backgroundColor: "var(--gold)", color: "#0f0f0f", borderRadius: "12px", minHeight: "48px", padding: "12px 16px" }}
          data-testid="google-review-btn"
        >
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Lasă un Google Review
        </a>
      </div>
    </div>

    {/* ── DESKTOP layout: slim horizontal ── */}
    <div className="hidden sm:flex max-w-[1140px] mx-auto px-4 lg:px-8 items-center justify-between gap-3" style={{ height: "52px" }}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex items-center gap-0.5 shrink-0">
          {[...Array(5)].map((_, i) => (
            <svg key={i} className="w-3.5 h-3.5" fill="#C6A85A" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
        <p className="text-white text-sm font-medium truncate">
          Ne-ați fost alături?{" "}
          <span style={{ color: "var(--gold)" }}>Spuneți-ne părerea dumneavoastră.</span>
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <a
          href="https://g.page/r/CRbaHIZUjJVVEBM/review"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold no-underline transition-all hover:opacity-90 hover:scale-105"
          style={{ backgroundColor: "var(--gold)", color: "#0f0f0f", borderRadius: "8px", whiteSpace: "nowrap" }}
          data-testid="google-review-btn"
        >
          <svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Lasă un Google Review
        </a>
        <button
          onClick={onDismiss}
          className="p-1 text-white/30 hover:text-white/70 bg-transparent border-none cursor-pointer"
          aria-label="Închide"
          data-testid="review-banner-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  </div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const bannerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const isHome = location.pathname === "/";
    const dismissed = localStorage.getItem(REVIEW_KEY);
    const show = isHome && !dismissed;
    setBannerVisible(show);
    if (!show) {
      document.documentElement.style.setProperty("--review-banner-height", "0px");
    }
  }, [location.pathname]);

  // Measure actual banner height for accurate hero offset
  useEffect(() => {
    if (!bannerVisible) return;
    const updateHeight = () => {
      if (bannerRef.current) {
        document.documentElement.style.setProperty("--review-banner-height", `${bannerRef.current.offsetHeight}px`);
      }
    };
    const t = setTimeout(updateHeight, 30);
    window.addEventListener("resize", updateHeight);
    return () => { clearTimeout(t); window.removeEventListener("resize", updateHeight); };
  }, [bannerVisible]);

  const dismissBanner = () => {
    localStorage.setItem(REVIEW_KEY, "1");
    setBannerVisible(false);
    document.documentElement.style.setProperty("--review-banner-height", "0px");
  };

  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "dark-header shadow-lg" : ""}`}
      style={{ backgroundColor: scrolled ? undefined : "var(--black)" }}
      data-testid="main-header"
    >
      <EmergencyBar />
      <nav className="max-w-[1140px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center no-underline" data-testid="logo-link">
            <img src={companyInfo.logo} alt="Casa Funerară Sfântul Andrei Constanța - Logo" className="h-16 w-auto" />
          </Link>
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 text-[13px] font-medium tracking-wide no-underline transition-colors duration-300 relative ${isActive(item.path) ? "text-white" : "text-white/55 hover:text-white"}`}
                style={isActive(item.path) ? { color: "var(--gold)" } : undefined}
                data-testid={`nav-${item.path.replace(/\//g, '') || 'home'}`}
              >
                {item.label}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-4 right-4 h-[1.5px]" style={{ backgroundColor: "var(--gold)" }} />
                )}
              </Link>
            ))}
          </div>
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 text-sm font-semibold tracking-wide no-underline transition-all duration-300 hover:shadow-lg px-6 py-2.5"
              style={{ borderRadius: "var(--radius-pill)", backgroundColor: "var(--gold)", color: "var(--black)" }}
              data-testid="header-cta-btn"
            >
              <Phone className="w-3.5 h-3.5" />
              Sună Acum
            </a>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-white bg-transparent border-none cursor-pointer"
            aria-label="Meniu navigare"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>
      {bannerVisible && <ReviewBanner onDismiss={dismissBanner} bannerRef={bannerRef} />}
      {mobileMenuOpen && (
        <div className="mobile-nav animate-fade-in">
          <button onClick={() => setMobileMenuOpen(false)} className="absolute top-6 right-6 p-2 bg-transparent border-none cursor-pointer" aria-label="Închide meniu">
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="flex flex-col items-center gap-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="text-xl no-underline tracking-wide"
                style={{ fontFamily: "'Playfair Display', serif", color: isActive(item.path) ? "var(--gold)" : "rgba(255,255,255,0.7)" }}
              >
                {item.label}
              </Link>
            ))}
            <a
              href={`tel:${companyInfo.phone}`}
              className="mt-4 flex items-center gap-3 px-8 py-3 text-base font-semibold no-underline"
              style={{ borderRadius: "var(--radius-pill)", backgroundColor: "var(--gold)", color: "var(--black)" }}
            >
              <Phone className="w-4 h-4" />
              {companyInfo.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer style={{ backgroundColor: "var(--black)" }} className="text-white" data-testid="main-footer">
    <div className="max-w-[1140px] mx-auto px-4 lg:px-8 pt-20 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
        <div className="lg:col-span-1">
          <div className="mb-6">
            <img src={companyInfo.logo} alt="Casa Funerară Sfântul Andrei Constanța - Logo" className="h-20 w-auto" />
          </div>
          <p className="text-sm text-white/50 leading-relaxed mb-6">{companyInfo.slogan}</p>
          <p className="text-xs text-white/30">Înființată în {companyInfo.founded} · Experiență {companyInfo.experience}</p>
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-[2px] mb-6" style={{ color: "var(--gold)" }}>Navigare</h4>
          <div className="flex flex-col gap-3">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} className="text-sm text-white/50 no-underline transition-colors duration-300" style={{ ["--tw-text-opacity"]: undefined }} onMouseEnter={e => e.target.style.color = "var(--gold)"} onMouseLeave={e => e.target.style.color = ""}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-[2px] mb-6" style={{ color: "var(--gold)" }}>Servicii Principale</h4>
          <div className="flex flex-col gap-3">
            {[
              { to: "/servicii/servicii-complete-inmormantare-constanta", label: "Înmormântare Completă" },
              { to: "/servicii/transport-funerar-international", label: "Transport Internațional" },
              { to: "/servicii/intocmire-acte-deces-documentatie", label: "Acte de Deces" },
              { to: "/servicii/servicii-funerare-non-stop", label: "Servicii NON-STOP" },
              { to: "/ajutor-inmormantare-2026", label: "Ajutor Înmormântare 2026" },
            ].map((item, i) => (
              <Link key={i} to={item.to} className="text-sm text-white/50 no-underline transition-colors" onMouseEnter={e => e.target.style.color = "var(--gold)"} onMouseLeave={e => e.target.style.color = ""}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-[2px] mb-6" style={{ color: "var(--gold)" }}>Contact</h4>
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Telefon NON-STOP</p>
              <a href={`tel:${companyInfo.phone}`} className="text-base font-semibold text-white no-underline hover:text-white/80">{companyInfo.phone}</a>
            </div>
            <div>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Telefon Secundar</p>
              <a href={`tel:${companyInfo.phoneSecondary}`} className="text-sm text-white/50 no-underline" onMouseEnter={e => e.target.style.color = "var(--gold)"} onMouseLeave={e => e.target.style.color = ""}>{companyInfo.phoneSecondary}</a>
            </div>
            <div>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Sediu Constanța</p>
              <p className="text-sm text-white/50">{companyInfo.addresses[0].street}, {companyInfo.addresses[0].city}</p>
            </div>
            <div>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Sediu Lumina</p>
              <p className="text-sm text-white/50">{companyInfo.addresses[1].street}, {companyInfo.addresses[1].city}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-xs text-white/30">© {new Date().getFullYear()} Casa Funerară Sfântul Andrei. Toate drepturile rezervate.</p>
          <p className="text-xs text-white/30">
            Realizat de <a href="https://webaz.ro/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[var(--gold)] no-underline transition-colors" style={{ color: "var(--gold)" }}>Webaz</a>
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(s => <span key={s} className="text-[10px]" style={{ color: "var(--gold)" }}>★</span>)}
          </div>
          <p className="text-xs text-white/30">5.0/5 pe Google · NON-STOP 24/7</p>
        </div>
      </div>
    </div>
  </footer>
);

const WhatsAppButton = () => {
  const waNumber = companyInfo.whatsapp.replace(/[^0-9]/g, '');
  const waText = encodeURIComponent('Bună ziua, am nevoie de informații despre serviciile funerare.');
  return (
    <a
      href={`https://wa.me/${waNumber}?text=${waText}`}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-btn"
      aria-label="Contactează-ne pe WhatsApp"
      data-testid="whatsapp-btn"
    >
      <MessageCircle className="w-6 h-6 text-white" fill="white" />
    </a>
  );
};

const StickyCallButton = () => (
  <a href={`tel:${companyInfo.phone}`} className="sticky-call-btn" data-testid="sticky-call-btn">
    <Phone className="w-4 h-4" />
    <span className="hidden sm:inline">Sună NON-STOP</span>
    <span className="sm:hidden">Sună</span>
  </a>
);

import CookieConsent from "./CookieConsent";

export const Layout = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
    <WhatsAppButton />
    <StickyCallButton />
    <CookieConsent />
  </div>
);

export default Layout;
